/**
 * Individual Clock View Page
 * Handles displaying individual clock posts with full-size images
 * VERSION: 2025-08-31-AI-ANALYSIS-RESTORED
 */
console.log('🚀 view-clock.js LOADED - AI Analysis Features Restored');
console.log('🔍 VERSION: 2025-08-31-AI-ANALYSIS-RESTORED');
console.log('🔍 TIMESTAMP:', new Date().toISOString());

class ClockViewPage {
    constructor() {
        this.clockSlug = this.getClockSlugFromUrl();
        this.clock = null;
        this.commentsUI = null;
        this.followStatus = null;
        this.currentUser = null;
        this.likeStatus = { isLiked: false, likeCount: 0 };
        
        if (this.clockSlug) {
            this.init();
        } else {
            this.showError('No clock slug provided', 'clock_not_found');
        }
    }

    /**
     * Initialize the page
     */
    async init() {
        try {
            await this.loadClockData();
            this.renderClock();
            this.setupEventListeners();
            this.updatePageTitle();
            await this.initFollowFeature();
            await this.initLikeFeature();
            
            // Track clock view with metadata
            if (window.analytics) {
                window.analytics.trackClockView(this.clock.id, this.clock.title, {
                    heritageGrade: this.clock.enrichment_data?.heritage?.grade || null,
                    council: this.clock.enrichment_data?.civic_context?.council || this.clock.council || null,
                    hasDeepResearch: !!this.clock.deepResearch
                });
            }
            
            // Initialize Street View modal
            this.initStreetViewModal();
            
            // Initialize comments system
            this.initComments();
            // Load and render working/stopped status controls
            this.loadAndRenderStatus().catch(err => console.warn('Status load error:', err));

            // Initialize insights panel (after clock data is loaded)
            this.initInsightsPanel();

            // Check for URL hash and open corresponding modal
            this.handleUrlHash();

        } catch (error) {
            console.error('Failed to initialize clock view:', error);
            this.showError('Failed to load clock details', 'clock_not_found');
        }
    }

    // (Removed live enforcement to avoid interaction freezes)

    /**
     * Handle URL hash fragments to open modals automatically
     */
    handleUrlHash() {
        const hash = window.location.hash;
        console.log('🔍 Checking URL hash:', hash);

        if (hash === '#research') {
            console.log('📖 #research hash detected, opening deep research modal');
            // Small delay to ensure deep research buttons are rendered
            setTimeout(() => {
                if (typeof window.viewDeepResearch === 'function' && this.clock?.id) {
                    window.viewDeepResearch(this.clock.id);
                } else {
                    console.warn('⚠️ Deep research not available yet');
                }
            }, 500);
        } else if (hash === '#enrichment' || hash === '#details') {
            console.log('🏛️ #enrichment hash detected, opening enrichment modal');
            // Small delay to ensure enrichment service is ready
            setTimeout(() => {
                if (window.enrichmentService && this.clock) {
                    window.enrichmentService.openEnrichmentModal(this.clock.id, this.clock);
                } else {
                    console.warn('⚠️ Enrichment service not available yet');
                }
            }, 500);
        }
    }

    /**
     * Get optimized WebP version of an image URL
     * @param {string} imageUrl - Original image URL
     * @param {string} size - Size variant (small/medium/large)
     * @returns {string} Optimized WebP URL or original URL
     */
    getOptimizedImageUrl(imageUrl, size = 'medium') {
        if (!imageUrl || typeof imageUrl !== 'string') {
            return imageUrl;
        }

        // Only optimize images in uploads/ folder
        if (!imageUrl.includes('/uploads/')) {
            return imageUrl;
        }

        // Skip if already a WebP file
        if (imageUrl.endsWith('.webp')) {
            return imageUrl;
        }

        // Map size to suffix
        const suffixMap = {
            'small': '_small',
            'medium': '_medium',
            'large': '_large'
        };

        const suffix = suffixMap[size] || '_medium';

        // Replace extension with WebP suffix
        // Example: uploads/image.jpg -> uploads/image_medium.webp
        const optimizedUrl = imageUrl.replace(/\.(jpg|jpeg|png)$/i, `${suffix}.webp`);

        return optimizedUrl;
    }

    /**
     * Initialize Street View modal functionality
     */
    initStreetViewModal() {
        this.streetViewModal = document.getElementById('streetViewModal');
        this.streetViewContainer = document.getElementById('streetViewContainer');
        this.streetViewTitle = document.getElementById('streetViewTitle');
        this.closeStreetViewBtn = document.getElementById('closeStreetView');
        
        if (!this.streetViewModal || !this.streetViewContainer || !this.closeStreetViewBtn) {
            console.warn('Street View modal elements not found');
            return;
        }
        
        // Close button event
        this.closeStreetViewBtn.addEventListener('click', () => {
            this.closeStreetViewModal();
        });
        
        // Close on backdrop click
        this.streetViewModal.addEventListener('click', (e) => {
            if (e.target === this.streetViewModal) {
                this.closeStreetViewModal();
            }
        });
        
        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.streetViewModal.classList.contains('active')) {
                this.closeStreetViewModal();
            }
        });
        
        console.log('Street View modal initialized');
    }
    
    /**
     * Initialize comments system
     */
    initComments() {
        try {
            if (this.clock && this.clock.id) {
                console.log('Initializing comments for clock:', this.clock.id);
                this.commentsUI = new CommentsUI('commentsContainer', this.clock.id);
            } else {
                console.warn('Clock ID not available, cannot initialize comments');
            }
        } catch (error) {
            console.error('Failed to initialize comments:', error);
        }
    }

    /**
     * Initialize insights panel
     */
    initInsightsPanel() {
        try {
            if (this.clock && this.clock.id) {
                console.log('📊 INSIGHTS: Initializing insights panel for clock:', this.clock.id);
                if (typeof ClockInsightsPanel !== 'undefined') {
                    ClockInsightsPanel.insertIntoClockView(this.clock.id)
                        .then(() => {
                            console.log('✅ INSIGHTS: Panel successfully initialized');
                        })
                        .catch(err => {
                            console.log('ℹ️ INSIGHTS: Not available for this clock:', err.message);
                        });
                } else {
                    console.warn('⚠️ INSIGHTS: ClockInsightsPanel component not loaded');
                }
            } else {
                console.warn('⚠️ INSIGHTS: Clock ID not available, cannot initialize insights');
            }
        } catch (error) {
            console.error('❌ INSIGHTS: Failed to initialize insights panel:', error);
        }
    }


    /**
     * Open Street View modal with specified coordinates
     */
    openStreetViewModal(lat, lng, title = 'Street View') {
        if (!this.streetViewModal || !this.streetViewContainer) {
            console.warn('Street View modal not initialized');
            return;
        }
        
        // Update modal title
        if (this.streetViewTitle) {
            this.streetViewTitle.textContent = title;
        }
        
        // Show modal
        this.streetViewModal.classList.add('active');
        document.body.style.overflow = 'hidden';
        
        // Load Street View panorama
        this.loadStreetViewPanorama(lat, lng);
        
        console.log(`Opening Street View modal for coordinates: ${lat}, ${lng}`);
    }
    
    /**
     * Close Street View modal
     */
    closeStreetViewModal() {
        if (!this.streetViewModal) return;
        
        this.streetViewModal.classList.remove('active');
        document.body.style.overflow = '';
        
        // Clear Street View content
        if (this.streetViewContainer) {
            this.streetViewContainer.innerHTML = '<div class="street-view-placeholder"><p>Loading Street View...</p></div>';
        }
        
        console.log('Street View modal closed');
    }
    
    /**
     * Load Google Street View panorama
     */
    loadStreetViewPanorama(lat, lng) {
        if (!this.streetViewContainer) {
            console.error('Street View container not found');
            return;
        }
        
        console.log('🔍 Loading Street View for coordinates:', lat, lng);
        
        // Check if Google Maps API is loaded
        if (typeof google === 'undefined') {
            console.error('Google Maps API not loaded - google object is undefined');
            this.streetViewContainer.innerHTML = `
                <div class="street-view-placeholder">
                    <p>Google Maps API not loaded. Please refresh the page.</p>
                    <p><small>Error: google object is undefined</small></p>
                </div>
            `;
            return;
        }
        
        if (!google.maps) {
            console.error('Google Maps API not loaded - google.maps is undefined');
            this.streetViewContainer.innerHTML = `
                <div class="street-view-placeholder">
                    <p>Google Maps API not loaded. Please refresh the page.</p>
                    <p><small>Error: google.maps is undefined</small></p>
                </div>
            `;
            return;
        }
        
        if (!google.maps.StreetViewPanorama) {
            console.error('Google Maps API not loaded - StreetViewPanorama is undefined');
            this.streetViewContainer.innerHTML = `
                <div class="street-view-placeholder">
                    <p>Google Maps API not loaded. Please refresh the page.</p>
                    <p><small>Error: StreetViewPanorama is undefined</small></p>
                </div>
            `;
            return;
        }
        
        try {
            // Create Street View panorama
            const panorama = new google.maps.StreetViewPanorama(
                this.streetViewContainer,
                {
                    position: { lat: parseFloat(lat), lng: parseFloat(lng) },
                    pov: {
                        heading: 34,
                        pitch: 10
                    },
                    zoom: 1,
                    addressControl: false,
                    fullscreenControl: false,
                    motionTracking: false,
                    motionTrackingControl: false,
                    panControl: true,
                    zoomControl: true
                }
            );
            
            // Handle panorama load errors
            google.maps.event.addListener(panorama, 'error', (error) => {
                console.warn('Street View panorama error:', error);
                this.streetViewContainer.innerHTML = `
                    <div class="street-view-placeholder">
                        <p>Street View not available at this location.</p>
                        <p>Coordinates: ${lat}, ${lng}</p>
                    </div>
                `;
            });
            
            // Handle panorama load success
            google.maps.event.addListener(panorama, 'pano_changed', () => {
                console.log('Street View panorama loaded successfully');
            });
            
        } catch (error) {
            console.error('Error creating Street View panorama:', error);
            this.streetViewContainer.innerHTML = `
                <div class="street-view-placeholder">
                    <p>Error loading Street View.</p>
                    <p>Please try again later.</p>
                </div>
            `;
        }
    }

    /**
     * Get clock slug from URL - supports clean URLs and query params
     * Clean URL format: /clocks/{slug}/
     * Legacy format: /view-clock.html?slug={slug}
     * @returns {string|null} Clock slug or null if not found
     */
    getClockSlugFromUrl() {
        // Check clean URL pattern first: /clocks/{slug}/
        const pathMatch = window.location.pathname.match(/^\/clocks\/([^\/]+)\/?$/);
        if (pathMatch) {
            return decodeURIComponent(pathMatch[1]);
        }

        // Fallback to query parameter (backward compatibility)
        const urlParams = new URLSearchParams(window.location.search);
        let slug = urlParams.get('slug');
        if (!slug) return null;
        // Sanitize slug if query string was malformed (e.g., slug=foo?nocache=1)
        if (slug.includes('?')) slug = slug.split('?')[0];
        if (slug.includes('&')) slug = slug.split('&')[0];
        if (slug.includes('#')) slug = slug.split('#')[0];
        return slug;
    }

    /**
     * Load clock data from DynamoDB API (with localStorage fallback)
     */
    async loadClockData() {
        try {
            console.log('Attempting to load clock data for slug:', this.clockSlug);
            console.log('DynamoDBService available:', typeof DynamoDBService !== 'undefined');
            
            // Try to load from DynamoDB API first
            if (typeof DynamoDBService !== 'undefined') {
                const dynamoDBService = new DynamoDBService();
                console.log('DynamoDBService instance created');
                
                // Try to get by slug first, then fallback to id if needed
                let result = await dynamoDBService.getClockBySlug(this.clockSlug);
                console.log('getClockBySlug result:', result);
                
                if (!result.success || !result.clock) {
                    // Fallback: try to get by ID if slug doesn't work
                    console.log('Trying fallback to getClock by ID');
                    result = await dynamoDBService.getClock(this.clockSlug);
                    console.log('getClock result:', result);
                }
                
                if (result.success && result.clock) {
                    this.clock = result.clock;
                    console.log('✅ Loaded clock data from DynamoDB:', this.clock);
                    return;
                } else {
                    throw new Error('Failed to load from DynamoDB API');
                }
            }
            
            // Fallback to localStorage if DynamoDB service not available
            console.warn('DynamoDB service not available, falling back to localStorage');
            const storedClocks = localStorage.getItem('stoppedclocks_data');
            console.log('localStorage data:', storedClocks);
            
            if (!storedClocks) {
                throw new Error('No clock data found');
            }

            const clocks = JSON.parse(storedClocks);
            console.log('Parsed clocks from localStorage:', clocks.length, 'clocks');
            console.log('Looking for slug:', this.clockSlug);
            
            // Try to find by slug first, then fallback to id
            this.clock = clocks.find(clock => clock.slug === this.clockSlug) || 
                        clocks.find(clock => clock.id === this.clockSlug);
            
            console.log('Found clock:', this.clock);
            
            if (!this.clock) {
                throw new Error('Clock not found');
            }

            console.log('Loaded clock data from localStorage:', this.clock);
        } catch (error) {
            console.error('Failed to load clock data:', error);
            throw error;
        }
    }

    /**
     * Render the clock view
     */
    renderClock() {
        const container = document.getElementById('clockView');
        if (!container) return;

        // Hide loading indicator
        const loadingIndicator = document.getElementById('clockLoading');
        if (loadingIndicator) {
            loadingIndicator.style.display = 'none';
        }

        // Debug: Log the clock data being rendered
        console.log('🔍 Rendering clock with data:', this.clock);
        
        // Update social sharing meta tags for this specific clock
        this.updateSocialSharing();

        // Inject JSON-LD structured data for SEO
        this.injectStructuredData();

        console.log('📝 Description field:', this.clock.description);
        console.log('📝 Description length:', this.clock.description ? this.clock.description.length : 'NO DESCRIPTION');
        console.log('📝 Description preview:', this.clock.description ? this.clock.description.substring(0, 200) + '...' : 'NO DESCRIPTION');
        console.log('🖼️ Image fields:', {
            imageUrl: this.clock.imageUrl,
            image: this.clock.image,
            thumbnailUrl: this.clock.thumbnailUrl
        });

        // Render tags with AI-generated tags styled differently
        const tagsHtml = this.clock.tags ? 
            this.clock.tags.map(tag => {
                // Check if this tag was AI-generated (case insensitive)
                const isAIGenerated = this.clock.aiGeneratedTags && 
                    this.clock.aiGeneratedTags.some(aiTag => 
                        aiTag.toLowerCase() === tag.toLowerCase()
                    );
                const tagClass = isAIGenerated ? 'tag ai-tag' : 'tag';
                return `<span class="${tagClass}" onclick="window.location.href='/?tag=${encodeURIComponent(tag)}'">${tag}</span>`;
            }).join('') : '';

        // Handle different data formats (DynamoDB vs localStorage)
        console.log('📅 Date debugging:', {
            createdAt: this.clock.createdAt,
            date: this.clock.date,
            datePosted: this.clock.datePosted,
            fullClock: this.clock
        });
        const dateStr = this.clock.createdAt || this.clock.date || this.clock.datePosted || new Date().toISOString();
        // Build author string strictly from canonical username when available
        const canonicalUsername = this.clock.postedByUsername || this.clock.authorUsername || null;
        // Fallback to email-derived username if no canonical username available
        const fallbackUsername = this.clock.postedBy && this.clock.postedBy.includes('@') 
            ? this.clock.postedBy.split('@')[0] 
            : null;
        const displayUsername = canonicalUsername || fallbackUsername;
        
        console.log('🔍 Author display debug:', {
            postedByUsername: this.clock.postedByUsername,
            authorUsername: this.clock.authorUsername,
            canonicalUsername: canonicalUsername,
            fallbackUsername: fallbackUsername,
            displayUsername: displayUsername,
            clock: this.clock
        });
        
        const authorStr = displayUsername
            ? `<a href="/public-profile.html?username=${encodeURIComponent(displayUsername)}" class="author-link">${displayUsername}</a>`
            : 'Unknown';
        
        // Handle multiple media files (new format) or single image (legacy)
        const images = this.clock.images || [];
        const videos = this.clock.videos || [];
        const mediaFiles = [...images, ...videos];
        const hasMultipleMedia = mediaFiles.length > 0;
        // Prefer thumbnail for initial render; full-size will be fetched on zoom
        const primaryImage = this.clock.thumbnailUrl || this.clock.thumbnail || this.clock.primaryImage || this.clock.imageUrl || this.clock.image;
        const fallbackImage = this.createFallbackImage(400, 300, 'No Image');
        
        // For legacy clocks with only a single image, create a media array
        const legacyImage = this.clock.thumbnailUrl || this.clock.thumbnail || this.clock.imageUrl || this.clock.image;
        const hasLegacyImage = legacyImage && !hasMultipleMedia;
        
        // Extract coordinates from different possible formats
        let latitude, longitude;
        if (this.clock.coordinates && this.clock.coordinates.latitude && this.clock.coordinates.longitude) {
            // New DynamoDB format
            latitude = this.clock.coordinates.latitude;
            longitude = this.clock.coordinates.longitude;
        } else if (this.clock.latitude && this.clock.longitude) {
            // Old format
            latitude = this.clock.latitude;
            longitude = this.clock.longitude;
        } else {
            // No coordinates available
            latitude = 'N/A';
            longitude = 'N/A';
        }

        container.innerHTML = `
            <div class="clock-header">
                <h1 class="clock-title">${this.clock.title}</h1>
                <div class="clock-meta">
                    <span class="clock-date">${this.formatDate(dateStr)}</span>
                    <span class="clock-author">Posted by ${authorStr}</span>
                    <span class="clock-status-badge" id="clockStatusBadge" style="display:none; margin-left:8px;"></span>
                    <button id="followButton" class="follow-btn" style="display:none;" onclick="clockView.toggleFollow()">
                        <span id="followButtonText">Follow</span>
                    </button>
                </div>
            </div>
            
            <div class="clock-content">
                ${hasMultipleMedia ? this.renderMediaGallery(mediaFiles, primaryImage, fallbackImage) : 
                  hasLegacyImage ? this.renderSingleImage(legacyImage, fallbackImage) : 
                  this.renderSingleImage(primaryImage, fallbackImage)}
                
                <!-- Upper action bar (moved controls) -->
                <div class="clock-action-bar" id="upperActionBar" style="display:flex; gap:8px; align-items:center; margin:12px 0 8px 0;"></div>
                
                <div class="clock-full-description">
                    <h3>Description</h3>
                    <div class="clock-description-content">${this.clock.description || 'No description available.'}</div>
                </div>
                
                <div class="clock-card-tags-enhanced" onclick="event.stopPropagation()">
                    ${tagsHtml}
                </div>

                ${this.renderAhsAttribution()}

                ${this.renderCCLicensing()}
                
                ${this.canUserModifyPost(this.clock) ? `
                    <div class="clock-edit-actions">
                        <a href="/admin/edit-clock.html?slug=${this.clock.slug || this.clock.id}" class="btn btn-primary">
                            Edit Post
                        </a>
                        ${this.checkAdminStatus() ? `
                            <button class="btn btn-danger" onclick="window.clockViewPage.deleteClock('${this.clock.id}')" style="margin-left: 10px;">
                                Delete
                            </button>
                        ` : `
                            <button class="btn btn-danger" onclick="window.clockViewPage.deleteClock('${this.clock.id}')" style="margin-left: 10px;">
                                Delete
                            </button>
                        `}
                    </div>
                ` : ''}
                

            </div>
        `;
        
        // Debug: Check if zoom button was created
        const debugZoomBtn = document.getElementById('zoomBtn');
        console.log('After rendering - Zoom button found:', debugZoomBtn);
        if (debugZoomBtn) {
            console.log('Zoom button properties:', {
                id: debugZoomBtn.id,
                className: debugZoomBtn.className,
                textContent: debugZoomBtn.textContent
            });
        }

        // Mount Discover More button and deep research controls in upper action bar
        const upperBar = document.getElementById('upperActionBar');
        if (upperBar) {
            const discoverBtn = document.createElement('button');
            discoverBtn.id = 'discoverMoreBtn';
            discoverBtn.className = 'btn btn-primary';
            discoverBtn.dataset.clockId = this.clock.id || this.clock.slug;
            discoverBtn.textContent = 'Clock Details';
            upperBar.appendChild(discoverBtn);
        }

        // Check for existing deep research data (will render into upperActionBar)
        // Always check regardless of login status - non-logged-in users can view completed research
        this.checkDeepResearchStatus();
    }

    /**
     * Render single image (legacy format)
     */
    renderSingleImage(imageSrc, fallbackImage) {
        const latitude = this.clock.coordinates?.latitude || this.clock.latitude || 'N/A';
        const longitude = this.clock.coordinates?.longitude || this.clock.longitude || 'N/A';

        // Use optimized large image for detail view
        const optimizedSrc = this.getOptimizedImageUrl(imageSrc, 'large');
        const originalSrc = imageSrc;

        return `
            <div class="clock-image-container">
                <img src="${optimizedSrc || fallbackImage}"
                     alt="${this.clock.title}"
                     class="clock-image"
                     loading="lazy"
                     ${originalSrc && originalSrc !== optimizedSrc ? `data-original="${originalSrc}"` : ''}
                     onerror="if(this.dataset.original && this.src !== this.dataset.original){this.src=this.dataset.original}else{this.src='${fallbackImage}'}">
                <div class="image-overlay">
                    <button class="btn btn-secondary zoom-btn" id="zoomBtn">Zoom</button>
                </div>
                <div class="like-heart-overlay" id="likeHeart" onclick="clockView.toggleLike()">
                    <span class="heart-icon">🤍</span>
                </div>
                <div class="image-actions-below">
                    ${latitude !== 'N/A' && longitude !== 'N/A' ?
                        `<button class="btn btn-secondary map-link street-view-btn" data-lat="${latitude}" data-lng="${longitude}" data-title="${this.clock.title}">
                            Explore in Street View
                        </button>` : ''
                    }
                    <span id="statusControlsMount"></span>
                </div>
            </div>
        `;
    }

    /**
     * Render media gallery (new multi-media format)
     */
    renderMediaGallery(mediaFiles, primaryImage, fallbackImage) {
        const allMedia = [...(this.clock.images || []), ...(this.clock.videos || [])];
        const primaryMedia = allMedia[0] || { url: primaryImage, type: 'image/jpeg' };
        const isPrimaryVideo = primaryMedia.type && primaryMedia.type.startsWith('video/');
        const originalUrl = primaryMedia.url || primaryImage || fallbackImage;
        const displayUrl = isPrimaryVideo ? originalUrl : this.getOptimizedImageUrl(originalUrl, 'large');
        const latitude = this.clock.coordinates?.latitude || this.clock.latitude || 'N/A';
        const longitude = this.clock.coordinates?.longitude || this.clock.longitude || 'N/A';

        return `
            <div class="clock-image-container">
                ${isPrimaryVideo ?
                    `<video class="clock-image" controls muted preload="metadata">
                        <source src="${displayUrl}" type="${primaryMedia.type}">
                        Your browser does not support the video tag.
                    </video>` :
                    `<img src="${displayUrl}"
                          alt="${this.clock.title}"
                          class="clock-image"
                          loading="lazy"
                          decoding="async"
                          ${originalUrl && originalUrl !== displayUrl ? `data-original="${originalUrl}"` : ''}
                          onerror="if(this.dataset.original && this.src !== this.dataset.original){this.src=this.dataset.original}else{this.src='${fallbackImage}'}">`
                }
                ${!isPrimaryVideo ? `
                    <div class="image-overlay">
                        <button class="btn btn-secondary zoom-btn" id="zoomBtn">Zoom</button>
                    </div>
                    <div class="like-heart-overlay" id="likeHeart" onclick="clockView.toggleLike()">
                        <span class="heart-icon">🤍</span>
                    </div>
                ` : ''}
                <div class="image-actions-below">
                    ${latitude !== 'N/A' && longitude !== 'N/A' ?
                        `<button class="btn btn-secondary map-link street-view-btn" data-lat="${latitude}" data-lng="${longitude}" data-title="${this.clock.title}">
                            Explore in Street View
                        </button>` : ''
                    }
                    <span id="statusControlsMount"></span>
                </div>
            </div>
            ${allMedia.length > 1 ? this.renderMediaThumbnails(allMedia) : ''}
        `;
    }

    /**
     * Render media thumbnails for gallery
     */
    renderMediaThumbnails(mediaFiles) {
        const thumbnails = mediaFiles.slice(0, 5).map((media, index) => {
            const isVideo = media.type && media.type.startsWith('video/');
            const thumbnail = media.thumbnail || media.url || media;
            return `
                <div class="media-thumbnail" data-index="${index}">
                    ${isVideo ? 
                        `<video class="thumbnail-video" muted><source src="${thumbnail}" type="${media.type}"></video>` :
                        `<img src="${thumbnail}" alt="Media ${index + 1}" class="thumbnail-image">`
                    }
                </div>
            `;
        }).join('');

        return `
            <div class="media-gallery-thumbnails">
                ${thumbnails}
            </div>
        `;
    }

    /**
     * Load comments and render status badge + toggle controls
     */
    async loadAndRenderStatus() {
        try {
            const clockId = this.clock?.id;
            if (!clockId || typeof CommentsService === 'undefined') return;
            const svc = new CommentsService();
            const resp = await svc.getComments(clockId);
            const comments = resp?.data?.comments || [];
            const status = this.deriveStatusFromComments(comments);
            this.renderStatusUI(status);
        } catch (e) {
            console.warn('Failed to load status from comments:', e);
        }
    }

    /**
     * Derive status from latest status-change comment
     * Falls back to clock.initialStatus for AHS clocks (unverified/stopped/fixed)
     */
    deriveStatusFromComments(comments) {
        // Check comments first (comment-derived status takes precedence)
        if (Array.isArray(comments) && comments.length > 0) {
            // Newest first
            const sorted = comments.slice().sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));
            const rx = /\bstatus\s*:\s*(fixed|stopped)\b/i;
            for (const c of sorted) {
                const text = String(c.content || '');
                const m = text.match(rx);
                if (m) {
                    const v = m[1].toLowerCase();
                    if (v === 'fixed' || v === 'stopped') return v;
                }
            }
        }
        // Fall back to database initialStatus (for AHS clocks)
        if (this.clock?.initialStatus) {
            return this.clock.initialStatus;
        }
        return 'unknown';
    }

    /**
     * Render badge and toggle controls based on status
     */
    renderStatusUI(status) {
        const isAhsClock = this.clock?.dataSource === 'ahs';

        // Badge
        const badge = document.getElementById('clockStatusBadge');
        if (badge) {
            if (status === 'fixed') {
                badge.style.display = 'inline-block';
                badge.style.background = '#e6ffed';
                badge.style.border = '1px solid #2ecc71';
                badge.style.color = '#1f7a3f';
                badge.style.padding = '2px 8px';
                badge.style.borderRadius = '999px';
                badge.textContent = 'Reported working';
            } else if (status === 'stopped') {
                badge.style.display = 'inline-block';
                badge.style.background = '#fff7e6';
                badge.style.border = '1px solid #f39c12';
                badge.style.color = '#8a4b08';
                badge.style.padding = '2px 8px';
                badge.style.borderRadius = '999px';
                badge.textContent = 'Reported stopped';
            } else if (status === 'unverified') {
                // AHS clocks with unverified status - hide badge, show label in controls area
                badge.style.display = 'none';
            } else {
                badge.style.display = 'none';
            }
        }

        // Controls (only if logged in)
        const mount = document.getElementById('statusControlsMount');
        if (!mount) return;
        if (!this.checkLoggedInStatus()) { mount.innerHTML = ''; return; }

        // AHS clocks with unverified status get different UI
        if (isAhsClock && status === 'unverified') {
            mount.innerHTML = `
                <span class="ahs-status-unknown-label" title="This clock was surveyed by the Turret Clock Guild. Help verify its current status.">Status unknown</span>
                <button id="markWorkingBtn" class="street-view-btn ahs-verify-btn" style="margin-left:8px;">Mark as working</button>
                <button id="markStoppedBtn" class="street-view-btn ahs-verify-btn" style="margin-left:4px;">Mark as stopped</button>
                <div id="statusCompose" style="display:none; margin-top:8px;">
                    <textarea id="statusReason" rows="2" placeholder="Add a short note (optional)" style="width:100%; max-width:520px;"></textarea>
                    <div style="margin-top:6px; display:flex; gap:8px;">
                        <button id="statusPostBtn" class="street-view-btn">Post</button>
                        <button id="statusCancelBtn" class="street-view-btn">Cancel</button>
                    </div>
                </div>`;

            const markWorkingBtn = document.getElementById('markWorkingBtn');
            const markStoppedBtn = document.getElementById('markStoppedBtn');
            const compose = document.getElementById('statusCompose');
            const postBtn = document.getElementById('statusPostBtn');
            const cancelBtn = document.getElementById('statusCancelBtn');

            let pendingStatus = null;

            markWorkingBtn?.addEventListener('click', () => {
                pendingStatus = 'fixed';
                compose.style.display = 'block';
            });
            markStoppedBtn?.addEventListener('click', () => {
                pendingStatus = 'stopped';
                compose.style.display = 'block';
            });
            cancelBtn?.addEventListener('click', () => {
                compose.style.display = 'none';
                pendingStatus = null;
            });
            postBtn?.addEventListener('click', () => {
                if (pendingStatus) {
                    this.submitAhsStatusVerification(pendingStatus);
                }
            });
        } else {
            // Standard UI for non-AHS or already verified clocks
            const primaryLabel = status === 'fixed' ? 'Report as stopped' : 'Mark as fixed';
            mount.innerHTML = `
                <button id="statusToggleBtn" class="street-view-btn" style="margin-left:8px;">${primaryLabel}</button>
                <div id="statusCompose" style="display:none; margin-top:8px;">
                    <textarea id="statusReason" rows="2" placeholder="Add a short note (optional)" style="width:100%; max-width:520px;"></textarea>
                    <div style="margin-top:6px; display:flex; gap:8px;">
                        <button id="statusPostBtn" class="street-view-btn">Post</button>
                        <button id="statusCancelBtn" class="street-view-btn">Cancel</button>
                    </div>
                </div>`;

            const toggleBtn = document.getElementById('statusToggleBtn');
            const compose = document.getElementById('statusCompose');
            const postBtn = document.getElementById('statusPostBtn');
            const cancelBtn = document.getElementById('statusCancelBtn');

            toggleBtn?.addEventListener('click', () => {
                compose.style.display = compose.style.display === 'none' ? 'block' : 'none';
            });
            cancelBtn?.addEventListener('click', () => { compose.style.display = 'none'; });
            postBtn?.addEventListener('click', () => this.submitStatusChange(status));
        }
    }

    /**
     * Submit AHS clock status verification (for unverified AHS clocks)
     */
    async submitAhsStatusVerification(newStatus) {
        try {
            if (typeof CommentsService === 'undefined') return alert('Comments unavailable.');
            const svc = new CommentsService();
            if (!svc.isAuthenticated()) {
                const returnUrl = encodeURIComponent(window.location.href);
                window.location.href = `/login.html?return=${returnUrl}`;
                return;
            }
            const reason = (document.getElementById('statusReason')?.value || '').trim();
            const prefix = `[status: ${newStatus}]`;
            const content = reason ? `${prefix} ${reason}` : prefix;
            const resp = await svc.createComment(this.clock.id, content);
            if (resp?.success) {
                // Update UI immediately - now shows standard controls
                this.renderStatusUI(newStatus);
                // If CommentsUI exists, refresh list
                if (this.commentsUI?.refresh) {
                    this.commentsUI.refresh().catch(()=>{});
                }
            } else {
                alert('Failed to save status.');
            }
        } catch (e) {
            console.error('Status verification error:', e);
            alert('An error occurred.');
        }
    }

    /**
     * Submit a status change as a comment
     */
    async submitStatusChange(currentStatus) {
        try {
            if (typeof CommentsService === 'undefined') return alert('Comments unavailable.');
            const svc = new CommentsService();
            if (!svc.isAuthenticated()) {
                const returnUrl = encodeURIComponent(window.location.href);
                window.location.href = `/login.html?return=${returnUrl}`;
                return;
            }
            const reason = (document.getElementById('statusReason')?.value || '').trim();
            const next = currentStatus === 'fixed' ? 'stopped' : 'fixed';
            const prefix = `[status: ${next}]`;
            const content = reason ? `${prefix} ${reason}` : prefix;
            const resp = await svc.createComment(this.clock.id, content);
            if (resp?.success) {
                // Update UI immediately
                this.renderStatusUI(next);
                // If CommentsUI exists, refresh list
                if (this.commentsUI?.refresh) {
                    this.commentsUI.refresh().catch(()=>{});
                }
            } else {
                alert('Failed to post status.');
            }
        } catch (e) {
            console.error('Status change failed:', e);
            alert('Failed to post status: ' + (e?.message || 'Unknown error'));
        }
    }

    /**
     * Check if deep research data exists for this clock
     */
    async checkDeepResearchStatus() {
        try {
            console.log('🔍 Checking deep research status for clock:', this.clock.id);
            
            const DEEP_RESEARCH_API_URL = 'https://rvnct9bwt6.execute-api.us-east-1.amazonaws.com/prod/deep-research';
            const response = await fetch(`${DEEP_RESEARCH_API_URL}?clockId=${this.clock.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                console.log('❌ Failed to check deep research status:', response.status);
                return;
            }

            const result = await response.json();
            console.log('📥 Deep research status response:', result);

            if (result.success && result.status === 'completed' && result.data) {
                console.log('✅ Deep research data found, updating buttons');
                this.updateDeepResearchButtons(true, result.data);
            } else if (result.success && result.status === 'in_progress') {
                console.log('⏳ Deep research in progress, showing cancel button');
                this.updateDeepResearchButtons('in_progress', result);
            } else {
                console.log('📝 No completed deep research data found');
                this.updateDeepResearchButtons(false);
            }

        } catch (error) {
            console.error('❌ Error checking deep research status:', error);
            // Don't update buttons on error, keep default state
        }
    }

    /**
     * Update the deep research buttons based on whether data exists
     * @param {boolean|string} hasData - Whether deep research data exists, or 'in_progress'
     * @param {Object} researchData - The research data (if available)
     */
    updateDeepResearchButtons(hasData, researchData = null) {
        // Find the flex container and remove any existing deep research buttons
        const flexContainer = document.getElementById('upperActionBar');
        if (!flexContainer) {
            console.log('⚠️ Upper action bar not found');
            return;
        }

        // Remove any existing deep research buttons using a data attribute selector
        // (more reliable than trying to match onclick handlers)
        const existingButtons = flexContainer.querySelectorAll('[data-deep-research-btn], .deep-research-status');
        existingButtons.forEach(btn => btn.remove());

        if (hasData === 'in_progress') {
            // Show cancel button when research is in progress (admin only)
            const isAdmin = this.checkAdminStatus();
            if (isAdmin) {
                const cancelBtn = document.createElement('button');
                cancelBtn.className = 'btn btn-secondary';
                cancelBtn.setAttribute('data-deep-research-btn', 'cancel');
                cancelBtn.onclick = () => cancelDeepResearch(this.clock.id);
                cancelBtn.innerHTML = '⏹️ Cancel Research';
                flexContainer.appendChild(cancelBtn);

                const statusSpan = document.createElement('span');
                statusSpan.className = 'deep-research-status';
                statusSpan.style.cssText = 'color: #dc3545; font-size: 14px; margin-left: 8px;';
                statusSpan.innerHTML = `Research in progress (ID: ${researchData.research_id || 'unknown'})`;
                flexContainer.appendChild(statusSpan);
                console.log('✅ Updated buttons: Cancel (admin) - research in progress');
            } else {
                const statusSpan = document.createElement('span');
                statusSpan.className = 'deep-research-status';
                statusSpan.style.cssText = 'color: #dc3545; font-size: 14px;';
                statusSpan.innerHTML = 'Research in progress...';
                flexContainer.appendChild(statusSpan);
                console.log('✅ Updated buttons: Status only (not admin) - research in progress');
            }
        } else if (hasData && researchData) {
            // Show View button for all users, Re-run button only for admin
            const isAdmin = this.checkAdminStatus();

            const viewBtn = document.createElement('button');
            viewBtn.className = 'btn btn-secondary';
            viewBtn.setAttribute('data-deep-research-btn', 'view');
            viewBtn.onclick = () => viewDeepResearch(this.clock.id);
            viewBtn.innerHTML = 'View Research';
            flexContainer.appendChild(viewBtn);

            if (isAdmin) {
                const rerunBtn = document.createElement('button');
                rerunBtn.className = 'btn btn-secondary';
                rerunBtn.setAttribute('data-deep-research-btn', 'rerun');
                rerunBtn.onclick = () => startDeepResearch(this.clock.id, true);
                rerunBtn.innerHTML = 'Re-run Research';
                flexContainer.appendChild(rerunBtn);
            }
            console.log('✅ Updated buttons: View + Re-run (admin only)');
        } else {
            // Show only start button when no data exists (admin only)
            const isAdmin = this.checkAdminStatus();
            if (isAdmin) {
                const startBtn = document.createElement('button');
                startBtn.className = 'btn btn-primary';
                startBtn.setAttribute('data-deep-research-btn', 'start');
                startBtn.onclick = () => startDeepResearch(this.clock.id);
                startBtn.innerHTML = 'Start Research';
                flexContainer.appendChild(startBtn);
                console.log('✅ Updated buttons: Start only (admin)');
            } else {
                console.log('✅ No buttons shown (not admin, no data)');
            }
        }
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Zoom button
        const zoomBtn = document.getElementById('zoomBtn');
        if (zoomBtn) {
            console.log('Setting up zoom button event listener');
            zoomBtn.addEventListener('click', () => {
                console.log('Zoom button clicked!');
                // Check if there are multiple media files
                const images = this.clock.images || [];
                const videos = this.clock.videos || [];
                const allMedia = [...images, ...videos];
                
                if (allMedia.length > 1) {
                    // Multiple media files - open gallery
                    this.openMediaGallery();
                } else {
                    // Single media file - open single image modal
                    this.openImageModal();
                }
            });
        } else {
            console.warn('Zoom button not found in DOM');
        }

        // Street View button event delegation
        const streetViewButtons = document.querySelectorAll('.street-view-btn');
        streetViewButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const lat = e.currentTarget.dataset.lat;
                const lng = e.currentTarget.dataset.lng;
                const title = e.currentTarget.dataset.title;
                this.openStreetViewModal(lat, lng, title);
            });
        });

        // Discover More button
        const discoverMoreBtn = document.getElementById('discoverMoreBtn');
        if (discoverMoreBtn) {
            console.log('Setting up discover more button event listener');
            discoverMoreBtn.addEventListener('click', () => {
                console.log('Discover More button clicked!');
                this.handleDiscoverMore();
            });
        } else {
            console.warn('Discover More button not found in DOM');
        }

        // Gallery button functionality is now handled by the zoom button

        // Media thumbnail clicks
        const thumbnails = document.querySelectorAll('.media-thumbnail');
        thumbnails.forEach((thumbnail, index) => {
            thumbnail.addEventListener('click', () => {
                this.selectMediaThumbnail(index);
            });
        });
    }

    /**
     * Open media gallery modal
     */
    openMediaGallery() {
        const images = this.clock.images || [];
        const videos = this.clock.videos || [];
        let allMedia = [...images, ...videos];
        
        // Handle legacy single image
        if (allMedia.length === 0) {
            const legacyImage = this.clock.imageUrl || this.clock.image || this.clock.thumbnailUrl;
            if (legacyImage) {
                allMedia = [{ url: legacyImage, type: 'image/jpeg' }];
            }
        }
        
        if (allMedia.length === 0) return;

        const modal = document.createElement('div');
        modal.className = 'media-gallery-modal active';
        modal.innerHTML = `
            <div class="gallery-modal-content">
                <button class="close-gallery">&times;</button>
                <div class="gallery-main-media-container">
                    <img class="gallery-main-media" id="galleryMainMedia" src="${allMedia[0].url || allMedia[0]}" alt="Gallery image">
                </div>
                <div class="gallery-nav">
                    <button class="btn btn-secondary" id="prevMedia">← Previous</button>
                    <span class="gallery-counter" id="galleryCounter">1 / ${allMedia.length}</span>
                    <button class="btn btn-secondary" id="nextMedia">Next →</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);
        this.currentMediaIndex = 0;
        this.galleryMedia = allMedia;

        // Event listeners
        modal.querySelector('.close-gallery').addEventListener('click', () => this.closeMediaGallery());
        modal.querySelector('#prevMedia').addEventListener('click', () => this.previousMedia());
        modal.querySelector('#nextMedia').addEventListener('click', () => this.nextMedia());
        
        // Close on backdrop click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) this.closeMediaGallery();
        });

        // Keyboard navigation
        document.addEventListener('keydown', this.handleGalleryKeydown.bind(this));
    }

    /**
     * Close media gallery modal
     */
    closeMediaGallery() {
        const modal = document.querySelector('.media-gallery-modal');
        if (modal) {
            document.body.removeChild(modal);
            document.removeEventListener('keydown', this.handleGalleryKeydown.bind(this));
        }
    }

    /**
     * Handle keyboard navigation in gallery
     */
    handleGalleryKeydown(e) {
        if (e.key === 'Escape') this.closeMediaGallery();
        if (e.key === 'ArrowLeft') this.previousMedia();
        if (e.key === 'ArrowRight') this.nextMedia();
    }

    /**
     * Navigate to previous media
     */
    previousMedia() {
        if (this.currentMediaIndex > 0) {
            this.currentMediaIndex--;
            this.updateGalleryDisplay();
        }
    }

    /**
     * Navigate to next media
     */
    nextMedia() {
        if (this.currentMediaIndex < this.galleryMedia.length - 1) {
            this.currentMediaIndex++;
            this.updateGalleryDisplay();
        }
    }

    /**
     * Update gallery display
     */
    updateGalleryDisplay() {
        const media = this.galleryMedia[this.currentMediaIndex];
        const mainMedia = document.getElementById('galleryMainMedia');
        const counter = document.getElementById('galleryCounter');
        
        if (media.type && media.type.startsWith('video/')) {
            mainMedia.outerHTML = `<video class="gallery-main-media" id="galleryMainMedia" controls><source src="${media.url || media}" type="${media.type}"></video>`;
        } else {
            mainMedia.outerHTML = `<img class="gallery-main-media" id="galleryMainMedia" src="${media.url || media}" alt="Gallery image">`;
        }
        
        if (counter) {
            counter.textContent = `${this.currentMediaIndex + 1} / ${this.galleryMedia.length}`;
        }
    }

    /**
     * Select media thumbnail
     */
    selectMediaThumbnail(index) {
        // Update thumbnail selection
        document.querySelectorAll('.media-thumbnail').forEach((thumb, i) => {
            thumb.classList.toggle('active', i === index);
        });

        // Update main media display
        const images = this.clock.images || [];
        const videos = this.clock.videos || [];
        const allMedia = [...images, ...videos];
        
        // Handle legacy single image
        if (allMedia.length === 0) {
            const legacyImage = this.clock.imageUrl || this.clock.image || this.clock.thumbnailUrl;
            if (legacyImage && index === 0) {
                const mainContainer = document.querySelector('.clock-image-container');
                if (mainContainer) {
                    const existingMedia = mainContainer.querySelector('.clock-image');
                    if (existingMedia) {
                        existingMedia.outerHTML = `<img src="${legacyImage}" alt="${this.clock.title}" class="clock-image" loading="lazy">`;
                    }
                }
            }
        } else if (allMedia[index]) {
            const media = allMedia[index];
            const isVideo = media.type && media.type.startsWith('video/');
            const mediaUrl = media.url || media;
            const mainContainer = document.querySelector('.clock-image-container');
            
            if (mainContainer) {
                const existingMedia = mainContainer.querySelector('.clock-image');
                if (existingMedia) {
                    if (isVideo) {
                        existingMedia.outerHTML = `<video class="clock-image" controls muted preload="metadata">
                            <source src="${mediaUrl}" type="${media.type}">
                            Your browser does not support the video tag.
                        </video>`;
                        // Update overlay for video
                        const imageOverlay = mainContainer.querySelector('.image-overlay');
                        const videoOverlay = mainContainer.querySelector('.video-overlay');
                        if (imageOverlay) {
                            imageOverlay.outerHTML = `<div class="video-overlay"></div>`;
                        } else if (!videoOverlay) {
                            mainContainer.insertAdjacentHTML('beforeend', `<div class="video-overlay"></div>`);
                        }
                    } else {
                        existingMedia.outerHTML = `<img src="${mediaUrl}" alt="${this.clock.title}" class="clock-image" loading="lazy">`;
                        // Update overlay for image
                        const videoOverlay = mainContainer.querySelector('.video-overlay');
                        const imageOverlay = mainContainer.querySelector('.image-overlay');
                        if (videoOverlay) {
                            videoOverlay.outerHTML = `<div class="image-overlay">
                                <button class="btn btn-secondary zoom-btn" id="zoomBtn">Zoom</button>
                            </div>`;
                        } else if (!imageOverlay) {
                            mainContainer.insertAdjacentHTML('beforeend', `<div class="image-overlay">
                                <button class="btn btn-secondary zoom-btn" id="zoomBtn">Zoom</button>
                            </div>`);
                        }
                        
                        // Re-setup zoom button event listener
                        const newZoomBtn = document.getElementById('zoomBtn');
                        if (newZoomBtn) {
                            newZoomBtn.addEventListener('click', () => {
                                console.log('Zoom button clicked!');
                                // Check if there are multiple media files
                                const images = this.clock.images || [];
                                const videos = this.clock.videos || [];
                                const allMedia = [...images, ...videos];
                                
                                if (allMedia.length > 1) {
                                    // Multiple media files - open gallery
                                    this.openMediaGallery();
                                } else {
                                    // Single media file - open single image modal
                                    this.openImageModal();
                                }
                            });
                        }
                    }
                }
            }
        }
    }

    /**
     * Redirect to gallery page with tag filter applied
     */
    redirectToGalleryWithTag(tagName) {
        // Encode the tag name for URL safety
        const encodedTag = encodeURIComponent(tagName);
        // Redirect to gallery page with tag filter
        window.location.href = `/?tag=${encodedTag}`;
    }

    /**
     * Create a fallback image as data URL
     */
    createFallbackImage(width, height, text = 'No Image') {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Background
        ctx.fillStyle = '#f8f9fa';
        ctx.fillRect(0, 0, width, height);
        
        // Border
        ctx.strokeStyle = '#dee2e6';
        ctx.lineWidth = 1;
        ctx.strokeRect(0.5, 0.5, width - 1, height - 1);
        
        // Text
        ctx.fillStyle = '#6c757d';
        ctx.font = `${Math.max(10, Math.min(width / 8, 14))}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Split text if it's too long
        const words = text.split(' ');
        let lines = [];
        let currentLine = '';
        
        for (let word of words) {
            const testLine = currentLine + (currentLine ? ' ' : '') + word;
            const metrics = ctx.measureText(testLine);
            if (metrics.width > width - 10) {
                if (currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    lines.push(word);
                }
            } else {
                currentLine = testLine;
            }
        }
        if (currentLine) {
            lines.push(currentLine);
        }
        
        // Draw text lines
        const lineHeight = Math.max(10, Math.min(width / 8, 14)) + 2;
        const totalHeight = lines.length * lineHeight;
        const startY = (height - totalHeight) / 2;
        
        // Ensure we don't exceed canvas height
        const maxLines = Math.floor((height - 20) / lineHeight);
        const displayLines = lines.slice(0, maxLines);
        
        displayLines.forEach((line, index) => {
            ctx.fillText(line, width / 2, startY + (index * lineHeight) + (lineHeight / 2));
        });
        
        return canvas.toDataURL();
    }

    /**
     * Open image in modal for full-size viewing
     */
    openImageModal() {
        console.log('Opening image modal for:', this.clock.title);
        console.log('Available image sources:', {
            imageUrl: this.clock.imageUrl,
            image: this.clock.image,
            thumbnailUrl: this.clock.thumbnailUrl
        });

        const modal = document.createElement('div');
        modal.className = 'image-modal';

        // For zoom modal, prefer the original high-resolution image
        const imageSrc = this.clock.imageUrl || this.clock.image || this.clock.thumbnailUrl || this.createFallbackImage(400, 300, 'No Image');
        console.log('Selected image source for modal:', imageSrc);

        // Get the position of the image container to center modal on it
        const imageContainer = document.querySelector('.clock-image-container');
        let scrollToPosition = 0;
        if (imageContainer) {
            const rect = imageContainer.getBoundingClientRect();
            scrollToPosition = window.pageYOffset + rect.top;
        }

        modal.innerHTML = `
            <div class="modal-content">
                <span class="close-modal">&times;</span>
                <img src="${imageSrc}" alt="${this.clock.title}" class="modal-image">
                <div class="modal-caption">
                    <h3>${this.clock.title}</h3>
                    <div class="modal-description-content">${this.clock.description}</div>
                </div>
            </div>
        `;

        // Close modal functionality
        const closeBtn = modal.querySelector('.close-modal');
        closeBtn.addEventListener('click', () => document.body.removeChild(modal));

        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                document.body.removeChild(modal);
            }
        });

        document.body.appendChild(modal);

        // Scroll to keep the modal centered on the image position
        if (scrollToPosition > 0) {
            window.scrollTo({ top: scrollToPosition - (window.innerHeight / 2) + 200, behavior: 'smooth' });
        }
    }

    /**
     * Share clock post
     */
    shareClock() {
        const shareData = {
            title: this.clock.title,
            text: this.clock.description,
            url: window.location.href
        };

        if (navigator.share) {
            navigator.share(shareData);
        } else {
            // Fallback: copy URL to clipboard
            navigator.clipboard.writeText(window.location.href).then(() => {
                alert('Link copied to clipboard!');
            }).catch(() => {
                // Fallback fallback: show URL
                prompt('Copy this link:', window.location.href);
            });
        }
    }

    /**
     * Open map view centered on clock location
     */
    openMapView() {
        // Extract coordinates using the same logic as the main display
        let latitude, longitude;
        if (this.clock.coordinates && this.clock.coordinates.latitude && this.clock.coordinates.longitude) {
            // New DynamoDB format
            latitude = this.clock.coordinates.latitude;
            longitude = this.clock.coordinates.longitude;
        } else if (this.clock.latitude && this.clock.longitude) {
            // Old format
            latitude = this.clock.latitude;
            longitude = this.clock.longitude;
        } else {
            // No coordinates available
            alert('Location data not available for this clock');
            return;
        }
        
        // Open Street View modal using local method
        this.openStreetViewModal(latitude, longitude, this.clock.title);
    }

    /**
     * Update social sharing meta tags for this specific clock
     */
    updateSocialSharing() {
        if (typeof SocialSharing !== 'undefined' && this.clock) {
            // Create a clock object with the data needed for social sharing
            const socialClock = {
                title: this.clock.title || 'Stopped Clock',
                description: this.clock.description || 'View details of this stopped public clock and learn about its history and restoration needs.',
                image: this.clock.imageUrl || this.clock.thumbnail || this.clock.image || '/images/stoppedclockslogo.png',
                location: this.clock.location || '',
                url: window.location.href
            };
            
            // Update social sharing meta tags
            if (window.socialSharing) {
                window.socialSharing.updateForClockPage(socialClock);
            } else {
                // Fallback: create a new instance
                const socialSharing = new SocialSharing();
                socialSharing.updateForClockPage(socialClock);
            }
            
            console.log('✅ Social sharing updated for clock:', socialClock.title);
        }
    }

    /**
     * Inject JSON-LD structured data for SEO
     * Creates Schema.org markup that helps Google understand the page content
     */
    injectStructuredData() {
        if (!this.clock) return;

        // Remove any existing JSON-LD
        const existing = document.querySelector('script[type="application/ld+json"]');
        if (existing) existing.remove();

        // Get the best available image URL
        const imageUrl = this.clock.imageUrl || this.clock.thumbnail ||
                         this.clock.image || 'https://stoppedclocks.org/images/stoppedclockslogo.png';
        const fullImageUrl = imageUrl.startsWith('http') ? imageUrl :
                             `https://stoppedclocks.org${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;

        // Build structured data
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "Article",
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": window.location.href
            },
            "headline": this.clock.title,
            "description": this.clock.description ||
                           `Stopped clock at ${this.clock.location || 'United Kingdom'}`,
            "image": fullImageUrl,
            "datePublished": this.clock.createdAt || this.clock.date,
            "dateModified": this.clock.updatedAt || this.clock.createdAt || this.clock.date,
            "author": {
                "@type": "Person",
                "name": this.clock.postedByUsername || "Anonymous"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Stopped Clocks",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://stoppedclocks.org/images/stoppedclockslogo.png"
                }
            },
            "about": {
                "@type": "Place",
                "name": this.clock.title,
                "address": this.clock.location || "United Kingdom"
            }
        };

        // Add heritage information if available
        if (this.clock.enrichment_data?.heritage?.grade) {
            structuredData.about.additionalProperty = {
                "@type": "PropertyValue",
                "name": "Heritage Grade",
                "value": this.clock.enrichment_data.heritage.grade
            };
        }

        // Add geo coordinates if available
        if (this.clock.latitude && this.clock.longitude) {
            structuredData.about.geo = {
                "@type": "GeoCoordinates",
                "latitude": this.clock.latitude,
                "longitude": this.clock.longitude
            };
        }

        // Add breadcrumbs for navigation
        structuredData.breadcrumb = {
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://stoppedclocks.org/"
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": this.clock.title,
                    "item": window.location.href
                }
            ]
        };

        // Inject into page head
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(structuredData, null, 2);
        document.head.appendChild(script);

        console.log('✅ JSON-LD structured data injected for SEO');
    }

    /**
     * Update page title with clock title
     */
    updatePageTitle() {
        if (this.clock) {
            document.title = `${this.clock.title} - Stopped Clocks`;
        }
    }

    /**
     * Format author display with link to profile
     * @param {string} authorEmail - Author's email address
     * @returns {string} HTML string with formatted author display
     */
    formatAuthorDisplay(authorEmail) {
        if (authorEmail === 'Unknown') return 'Unknown';
        
        // Prefer postedByUsername; fallback to part before @; final fallback anonymised
        const username = (this.clock && this.clock.postedByUsername)
            ? this.clock.postedByUsername
            : (authorEmail && authorEmail.includes('@') ? authorEmail.split('@')[0] : 'unknown');
        
        
        // Create a link to the public profile page using username instead of email
        return `<a href="/public-profile.html?username=${encodeURIComponent(username)}" class="author-link">${username}</a>`;
    }

    /**
     * Format date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date
     */
    formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Show error message with enhanced 404 handling
     * @param {string} message - Error message to display
     * @param {string} type - Error type (clock_not_found, permission_denied, etc.)
     */
    showError(message, type = 'generic') {
        const container = document.getElementById('clockView');
        if (container) {
            const errorContent = this.getErrorContent(message, type);
            container.innerHTML = errorContent;
        }
    }

    /**
     * Get enhanced error content based on error type
     */
    getErrorContent(message, type) {
        const clockSlug = this.getClockSlugFromUrl();
        
        switch (type) {
            case 'clock_not_found':
                return `
                    <div class="error-404">
                        <div class="error-header">
                            <h1>Clock Not Found</h1>
                            <p>We couldn't find "${clockSlug || 'this clock'}"</p>
                        </div>
                        
                        <div class="error-suggestions">
                            <h3>What you can do:</h3>
                            <div class="suggestion-search">
                                <h4>Search for similar clocks</h4>
                                <div class="search-box">
                                    <input type="text" id="errorSearchInput" placeholder="Search clocks..." />
                                    <button onclick="window.clockView.performErrorSearch()" class="btn btn-primary">Search</button>
                                </div>
                            </div>
                            
                            <div class="suggestion-tags">
                                <h4>🏷️ Browse by tags</h4>
                                <div class="tag-suggestions" id="errorTagSuggestions">
                                    <span class="tag" onclick="window.location.href='/?tag=clock'">clock</span>
                                    <span class="tag" onclick="window.location.href='/?tag=tower'">tower</span>
                                    <span class="tag" onclick="window.location.href='/?tag=church'">church</span>
                                    <span class="tag" onclick="window.location.href='/?tag=station'">station</span>
                                </div>
                            </div>
                            
                            <div class="suggestion-popular">
                                <h4>⭐ Popular clocks</h4>
                                <div class="popular-clocks" id="errorPopularClocks">
                                    <p>Loading popular clocks...</p>
                                </div>
                            </div>
                        </div>
                        
                        <div class="error-actions">
                            <a href="/" class="btn btn-primary">🏠 Return to Gallery</a>
                            <button onclick="window.history.back()" class="btn btn-secondary">← Go Back</button>
                        </div>
                    </div>
                `;
                
            case 'permission_denied':
                return `
                    <div class="error-403">
                        <div class="error-header">
                            <h1>🔒 Access Denied</h1>
                            <p>You need to log in to view this content</p>
                        </div>
                        <div class="error-actions">
                            <a href="/login-working.html?return=${encodeURIComponent(window.location.href)}" class="btn btn-primary">🔑 Log In</a>
                            <a href="/register.html" class="btn btn-secondary">📝 Create Account</a>
                            <a href="/" class="btn btn-outline">🏠 Return to Gallery</a>
                        </div>
                    </div>
                `;
                
            default:
                return `
                    <div class="error-message">
                        <h2>⚠️ Error</h2>
                        <p>${message}</p>
                        <div class="error-actions">
                            <a href="/" class="btn btn-primary">🏠 Return to Gallery</a>
                            <button onclick="window.history.back()" class="btn btn-secondary">← Go Back</button>
                        </div>
                    </div>
                `;
        }
    }

    /**
     * Perform search from error page
     */
    performErrorSearch() {
        const searchInput = document.getElementById('errorSearchInput');
        if (searchInput && searchInput.value.trim()) {
            const searchTerm = searchInput.value.trim();
            window.location.href = `/?search=${encodeURIComponent(searchTerm)}`;
        }
    }

    /**
     * Load popular clocks for error page
     */
    async loadPopularClocksForError() {
        try {
            // This would load featured or popular clocks
            // For now, we'll show a simple message
            const container = document.getElementById('errorPopularClocks');
            if (container) {
                container.innerHTML = `
                    <p>Check out our <a href="/">gallery</a> to see all available clocks</p>
                `;
            }
        } catch (error) {
            console.error('Error loading popular clocks:', error);
        }
    }

    /**
     * Check if the current user is logged in
     */
    checkLoggedInStatus() {
        const userInfo = this.getUserInfo();
        const authToken = localStorage.getItem('authToken');
        
        // Must have both user info and valid auth token
        if (!userInfo || !authToken) {
            return false;
        }
        
        // Check if token is expired
        try {
            const tokenData = JSON.parse(atob(authToken.split('.')[1]));
            const now = Math.floor(Date.now() / 1000);
            if (tokenData.exp < now) {
                return false;
            }
        } catch (error) {
            console.log('Token validation error:', error);
            return false;
        }
        
        return true;
    }

    /**
     * Check if the current user is an admin
     */
    checkAdminStatus() {
        const userInfo = this.getUserInfo();
        const authToken = localStorage.getItem('authToken');
        
        // Must have both user info and valid auth token
        if (!userInfo || !authToken) {
            console.log('Admin check failed: missing user info or auth token');
            return false;
        }
        
        // Check if token is expired
        try {
            const tokenData = JSON.parse(atob(authToken.split('.')[1]));
            const now = Math.floor(Date.now() / 1000);
            if (tokenData.exp < now) {
                console.log('Admin check failed: token expired');
                return false;
            }
        } catch (e) {
            console.log('Admin check failed: invalid token');
            return false;
        }
        
        // Check if user is admin
        return userInfo.role === 'admin';
    }

    /**
     * Check if current user owns a specific post
     */
    isOwnedByCurrentUser(clock) {
        const userInfo = this.getUserInfo();
        if (!userInfo || !clock.postedBy) {
            return false;
        }
        
        // Use canonical username for ownership check, fallback to email if needed
        const clockAuthor = clock.postedByUsername || clock.authorUsername || clock.postedBy;
        const isOwner = userInfo.email === clock.postedBy || userInfo.username === clockAuthor;
        console.log(`Ownership check for clock ${clock.id}:`, {
            userEmail: userInfo.email,
            userUsername: userInfo.username,
            clockPostedBy: clock.postedBy,
            clockAuthor: clockAuthor,
            isOwner: isOwner
        });
        
        return isOwner;
    }

    /**
     * Check if current user can edit/delete a specific post
     */
    canUserModifyPost(clock) {
        console.log('=== canUserModifyPost DEBUG START ===');
        console.log('Checking permissions for clock:', clock.id);
        
        const isAdmin = this.checkAdminStatus();
        const isOwner = this.isOwnedByCurrentUser(clock);
        
        const canModify = isAdmin || isOwner;
        const userInfo = this.getUserInfo();
        const clockAuthor = clock.postedByUsername || clock.authorUsername || clock.postedBy;
        console.log(`Modification check for clock ${clock.id}:`, {
            isAdmin: isAdmin,
            isOwner: isOwner,
            canModify: canModify,
            clockPostedBy: clock.postedBy,
            clockAuthor: clockAuthor,
            currentUserEmail: userInfo?.email,
            currentUserUsername: userInfo?.username
        });
        
        console.log('=== canUserModifyPost DEBUG END ===');
        return canModify;
    }

    /**
     * Get user info from localStorage
     */
    getUserInfo() {
        // Prefer sessionStorage (per docs TokenManager), fallback to localStorage
        const raw = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('userInfo'))
            || localStorage.getItem('userInfo');
        // Handle case where localStorage contains string "undefined" or "null"
        if (raw && raw !== 'undefined' && raw !== 'null') {
            try {
                return JSON.parse(raw);
            } catch (e) {
                console.error('Error parsing user info:', e);
                return null;
            }
        }
        return null;
    }

    /**
     * Delete a clock (admin or owner only)
     */
    async deleteClock(clockId) {
        console.log('deleteClock called with clockId:', clockId);
        
        const userInfo = this.getUserInfo();
        const authToken = localStorage.getItem('authToken');
        console.log('Current authentication state:', {
            userInfo: userInfo,
            authToken: authToken ? 'EXISTS' : 'MISSING',
            isAuthenticated: !!userInfo && !!authToken,
            isAdmin: this.checkAdminStatus()
        });
        
        // Find the clock to check ownership
        if (!this.clock || this.clock.id !== clockId) {
            console.error('Clock not found in current view');
            alert('Clock not found.');
            return;
        }
        
        console.log('Clock found:', this.clock);
        
        // Check if user can modify this post (admin or owner)
        const canModify = this.canUserModifyPost(this.clock);
        console.log('Permission check result:', canModify);
        
        if (!canModify) {
            console.error('User does not have permission to delete this clock');
            alert('You do not have permission to delete this clock.');
            return;
        }

        if (!confirm('Are you sure you want to delete this clock? This action cannot be undone.')) {
            console.log('User cancelled deletion');
            return;
        }

        try {
            console.log('Attempting to delete clock:', clockId);
            
            // Use DynamoDB service to delete the clock
            const dynamoDBService = new DynamoDBService();
            const result = await dynamoDBService.deleteClock(clockId);
            
            if (result.success) {
                console.log('Clock deleted successfully');
                alert('Clock deleted successfully.');
                
                // Redirect to gallery
                window.location.href = '/';
            } else {
                console.error('Failed to delete clock:', result.error);
                alert('Failed to delete clock: ' + (result.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error deleting clock:', error);
            alert('Error deleting clock: ' + error.message);
        }
    }

    /**
     * Handle Discover More button click
     * This will trigger the AI enrichment process
     */
    handleDiscoverMore() {
        console.log('🚀 Starting Discover More enrichment for clock:', this.clock.id || this.clock.slug);
        console.log('🔍 Clock data available:', this.clock);
        console.log('🔍 window.enrichmentService exists:', !!window.enrichmentService);
        console.log('🔍 window.enrichmentService type:', typeof window.enrichmentService);
        console.log('🔍 window.enrichmentService value:', window.enrichmentService);
        
        // Check if enrichment service is available
        if (window.enrichmentService) {
            console.log('✅ Enrichment service found, calling openEnrichmentModal');
            try {
                window.enrichmentService.openEnrichmentModal(this.clock.id || this.clock.slug, this.clock);
            } catch (error) {
                console.error('❌ Error calling enrichment service:', error);
                this.showEnrichmentPlaceholder();
            }
        } else {
            console.log('⚠️ Enrichment service not found, waiting 1 second...');
            // Wait a bit for the service to initialize
            setTimeout(() => {
                console.log('🔍 After timeout - window.enrichmentService exists:', !!window.enrichmentService);
                console.log('🔍 After timeout - window.enrichmentService type:', typeof window.enrichmentService);
                
                if (window.enrichmentService) {
                    console.log('✅ Enrichment service found after timeout, calling openEnrichmentModal');
                    try {
                        window.enrichmentService.openEnrichmentModal(this.clock.id || this.clock.slug, this.clock);
                    } catch (error) {
                        console.error('❌ Error calling enrichment service after timeout:', error);
                        this.showEnrichmentPlaceholder();
                    }
                } else {
                    console.error('❌ Enrichment service not available after timeout');
                    console.log('🔍 Available window objects:', Object.keys(window).filter(key => key.includes('enrichment')));
                    this.showEnrichmentPlaceholder();
                }
            }, 1000);
        }
    }

    /**
     * Show enrichment placeholder while we implement the full feature
     */
    showEnrichmentPlaceholder() {
        // Create a temporary enrichment section
        const clockDetails = document.querySelector('.clock-details');
        if (clockDetails) {
            const enrichmentSection = document.createElement('div');
            enrichmentSection.className = 'enrichment-section';
            enrichmentSection.innerHTML = `
                <div class="enrichment-placeholder">
                    <h3>AI-Powered Clock Enrichment</h3>
                    <p>This feature is coming soon! Our AI will analyze this clock and provide:</p>
                    <ul>
                        <li>Building identification and history</li>
                        <li>Precise location details</li>
                        <li>Clock features and specifications</li>
                        <li>Contact information</li>
                        <li>Heritage status</li>
                    </ul>
                    <p><em>This will use our advanced AI analysis system with Google Places, Geocoding, and GPT-5 synthesis.</em></p>
                </div>
            `;
            
            // Insert after the description
            const description = clockDetails.querySelector('.clock-full-description');
            if (description) {
                description.parentNode.insertBefore(enrichmentSection, description.nextSibling);
            }

            // Re-enable the button
            const discoverMoreBtn = document.getElementById('discoverMoreBtn');
            if (discoverMoreBtn) {
                discoverMoreBtn.disabled = false;
                discoverMoreBtn.textContent = 'Clock Details';
            }
        }
    }

    /**
     * Initialize follow functionality
     */
    async initFollowFeature() {
        try {
            this.currentUser = this.getUserInfo();
            console.log('🔍 Follow init - Current user:', this.currentUser);
            console.log('🔍 Follow init - Auth token exists:', !!this.getAuthToken());
            
            if (!this.currentUser) {
                console.log('No user logged in, hiding follow button');
                return;
            }

            // Get the author username from the clock
            // Prefer canonical username fields; if missing, try to enrich from /clocks/{id}
            let authorUsername = this.clock?.postedByUsername || this.clock?.authorUsername || null;
            if (!authorUsername) {
                try {
                    if (typeof DynamoDBService !== 'undefined' && this.clock?.id) {
                        const dynamoDBService = new DynamoDBService();
                        const byId = await dynamoDBService.getClock(this.clock.id);
                        if (byId?.success && (byId.clock?.postedByUsername || byId.clock?.authorUsername)) {
                            authorUsername = byId.clock.postedByUsername || byId.clock.authorUsername;
                            // persist on instance for later renders
                            this.clock.postedByUsername = authorUsername;
                        }
                    }
                } catch (e) {
                    console.warn('Author username enrichment failed:', e);
                }
            }
            if (!authorUsername) return; // Skip follow UI if we still don't have a canonical username
            
            // Don't show follow button for own posts
            if (this.currentUser.username === authorUsername) {
                console.log('Own post, hiding follow button');
                return;
            }

            // Show follow button and check follow status
            const followButton = document.getElementById('followButton');
            if (followButton) {
                followButton.style.display = 'inline-block';
                console.log('🔍 Follow init - using authorUsername:', authorUsername);
                // Guard: do not call status if looks like email-derived username
                console.log('🔍 Username validation:', {
                    authorUsername: authorUsername,
                    hasAt: authorUsername?.includes('@'),
                    hasDot: authorUsername?.includes('.'),
                    willCallStatus: authorUsername && !authorUsername.includes('@') && !authorUsername.includes('.')
                });
                if (authorUsername && !authorUsername.includes('@') && !authorUsername.includes('.')) {
                    console.log('🔍 Calling checkFollowStatus for:', authorUsername);
                    await this.checkFollowStatus(authorUsername);
                } else {
                    console.warn('Skipping status check due to non-canonical username:', authorUsername);
                }
            }
        } catch (error) {
            console.error('Error initializing follow feature:', error);
        }
    }

    /**
     * Check if current user follows the author
     * @param {string} authorUsername - Username of the author
     */
    async checkFollowStatus(authorUsername) {
        try {
            const token = this.getAuthToken();
            if (!token) {
                console.log('Skipping follow status check: no auth token');
                return;
            }
            console.log('🔍 checkFollowStatus - authorUsername:', authorUsername);
            if (!authorUsername || authorUsername.includes('@') || authorUsername.includes('.')) {
                console.warn('checkFollowStatus blocked: non-canonical username:', authorUsername);
                return;
            }
            const response = await fetch(`https://7zty3xp0k7.execute-api.us-east-1.amazonaws.com/prod/follow/status?followingUsername=${encodeURIComponent(authorUsername)}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                console.log('🔍 Follow status API response:', result);
                this.followStatus = result.data.isFollowing;
                console.log('🔍 Follow status set to:', this.followStatus);
                this.updateFollowButton();
            } else {
                console.error('Failed to check follow status:', response.status);
                const errorText = await response.text();
                console.error('Follow status error response:', errorText);
            }
        } catch (error) {
            console.error('Error checking follow status:', error);
        }
    }

    /**
     * Update follow button appearance based on follow status
     */
    updateFollowButton() {
        const followButton = document.getElementById('followButton');
        const followButtonText = document.getElementById('followButtonText');
        
        if (followButton && followButtonText) {
            followButton.classList.toggle('following', !!this.followStatus);
            followButtonText.textContent = this.followStatus ? 'Following' : 'Follow';
        }
    }

    /**
     * Toggle follow status
     */
    async toggleFollow() {
        try {
            let authorUsername = this.clock?.postedByUsername || this.clock?.authorUsername || null;
            if (!authorUsername && this.clock?.id && typeof DynamoDBService !== 'undefined') {
                try {
                    const dynamoDBService = new DynamoDBService();
                    const byId = await dynamoDBService.getClock(this.clock.id);
                    if (byId?.success && (byId.clock?.postedByUsername || byId.clock?.authorUsername)) {
                        authorUsername = byId.clock.postedByUsername || byId.clock.authorUsername;
                        this.clock.postedByUsername = authorUsername;
                    }
                } catch (e) {
                    console.warn('Author username enrichment failed:', e);
                }
            }
            if (!authorUsername) return;
            
            if (this.followStatus) {
                await this.unfollowUser(authorUsername);
            } else {
                await this.followUser(authorUsername);
            }
        } catch (error) {
            console.error('Error toggling follow:', error);
            this.showFollowError('Failed to update follow status');
        }
    }

    /**
     * Follow a user
     * @param {string} username - Username to follow
     */
    async followUser(username) {
        try {
            const authToken = this.getAuthToken();
            if (!authToken) {
                this.showFollowError('You must be logged in to follow');
                return;
            }
            if (!username || username.includes('@') || username.includes('.')) {
                this.showFollowError('Cannot follow: username not available');
                return;
            }
            console.log('🔍 Follow attempt - Auth token exists:', !!authToken);
            console.log('🔍 Follow attempt - Token preview:', authToken ? authToken.substring(0, 20) + '...' : 'null');
            
            const response = await fetch('https://7zty3xp0k7.execute-api.us-east-1.amazonaws.com/prod/follow', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    followingUsername: username
                })
            });

            if (response.ok) {
                this.followStatus = true;
                this.updateFollowButton();
                console.log(`Successfully followed ${username}`);
            } else {
                const error = await response.json();
                console.error('Failed to follow user:', error);
                this.showFollowError(error.error || 'Failed to follow user');
            }
        } catch (error) {
            console.error('Error following user:', error);
            this.showFollowError('Failed to follow user');
        }
    }

    /**
     * Unfollow a user
     * @param {string} username - Username to unfollow
     */
    async unfollowUser(username) {
        try {
            const response = await fetch('https://7zty3xp0k7.execute-api.us-east-1.amazonaws.com/prod/follow', {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${this.getAuthToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    followingUsername: username
                })
            });

            if (response.ok) {
                this.followStatus = false;
                this.updateFollowButton();
                console.log(`Successfully unfollowed ${username}`);
            } else {
                const error = await response.json();
                console.error('Failed to unfollow user:', error);
                this.showFollowError(error.error || 'Failed to unfollow user');
            }
        } catch (error) {
            console.error('Error unfollowing user:', error);
            this.showFollowError('Failed to unfollow user');
        }
    }

    /**
     * Show follow error message
     * @param {string} message - Error message to show
     */
    showFollowError(message) {
        // You could implement a toast notification or alert here
        console.error('Follow error:', message);
        // For now, just log to console - you might want to show a user-friendly message
    }

    /**
     * Initialize like feature
     */
    async initLikeFeature() {
        try {
            this.currentUser = this.getUserInfo();
            console.log('🔍 Like init - Current user:', this.currentUser);
            
            if (!this.currentUser) {
                console.log('No user logged in, hiding like button');
                const likeButton = document.getElementById('likeButton');
                if (likeButton) {
                    likeButton.style.display = 'none';
                }
                return;
            }

            // Load like status and count
            await this.loadLikeStatus();
        } catch (error) {
            console.error('Error initializing like feature:', error);
        }
    }

    /**
     * Load like status and count for the current clock
     */
    async loadLikeStatus() {
        try {
            const token = this.getAuthToken();
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`https://7zty3xp0k7.execute-api.us-east-1.amazonaws.com/prod/likes/${this.clock.id}?action=status`, {
                method: 'GET',
                headers: headers
            });

            if (response.ok) {
                const result = await response.json();
                console.log('🔍 Like status API response:', result);
                this.likeStatus = {
                    isLiked: result.data.isLiked || false,
                    likeCount: result.data.likeCount || 0
                };
                this.updateLikeButton();
            } else {
                console.error('Failed to load like status:', response.status);
                const errorText = await response.text();
                console.error('Like status error response:', errorText);
                // Set default values if API fails
                this.likeStatus = {
                    isLiked: false,
                    likeCount: 0
                };
                this.updateLikeButton();
            }
        } catch (error) {
            console.error('Error loading like status:', error);
            // Set default values if API fails
            this.likeStatus = {
                isLiked: false,
                likeCount: 0
            };
            this.updateLikeButton();
        }
    }

    /**
     * Update like heart appearance based on like status
     */
    updateLikeButton() {
        const likeHeart = document.getElementById('likeHeart');
        const heartIcon = likeHeart?.querySelector('.heart-icon');

        if (likeHeart && heartIcon) {
            // Toggle .liked class for CSS styling
            likeHeart.classList.toggle('liked', !!this.likeStatus.isLiked);

            // Change emoji based on liked state
            // 🤍 = white/outline heart (unliked)
            // ❤️ = red filled heart (liked)
            heartIcon.textContent = this.likeStatus.isLiked ? '❤️' : '🤍';
        }
    }

    /**
     * Toggle like status
     */
    async toggleLike() {
        try {
            const token = this.getAuthToken();
            if (!token) {
                // Redirect to register page if not logged in
                window.location.href = '/register.html';
                return;
            }

            const action = this.likeStatus.isLiked ? 'unlike' : 'like';
            
            const response = await fetch(`https://7zty3xp0k7.execute-api.us-east-1.amazonaws.com/prod/likes/${this.clock.id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action })
            });

            if (response.ok) {
                const result = await response.json();
                console.log('🔍 Like toggle API response:', result);
                this.likeStatus = {
                    isLiked: result.data.isLiked,
                    likeCount: result.data.likeCount
                };
                this.updateLikeButton();
                console.log(`Successfully ${action}d clock`);
            } else {
                const error = await response.json();
                console.error(`Failed to ${action} clock:`, error);
                this.showLikeError(error.error || `Failed to ${action} clock`);
            }
        } catch (error) {
            console.error(`Error ${this.likeStatus.isLiked ? 'unliking' : 'liking'} clock:`, error);
            this.showLikeError(`Failed to ${this.likeStatus.isLiked ? 'unlike' : 'like'} clock`);
        }
    }

    /**
     * Show like error message
     * @param {string} message - Error message to show
     */
    showLikeError(message) {
        // You could implement a toast notification or alert here
        console.error('Like error:', message);
        // For now, just log to console - you might want to show a user-friendly message
    }

    /**
     * Get authentication token from localStorage
     * @returns {string|null} JWT token or null
     */
    getAuthToken() {
        // Per docs TokenManager stores in sessionStorage; fallback to localStorage
        if (typeof sessionStorage !== 'undefined') {
            const token = sessionStorage.getItem('authToken');
            if (token) return token;
        }
        return localStorage.getItem('authToken');
    }

    /**
     * Render AHS Turret Clock Guild attribution section (only for AHS clocks)
     * @returns {string} HTML for AHS attribution display or empty string
     */
    renderAhsAttribution() {
        // Only show for AHS clocks
        if (this.clock.dataSource !== 'ahs' || !this.clock.ahsData) {
            return '';
        }

        const ahs = this.clock.ahsData;

        // Build data rows
        const rows = [];

        // TCG database link - always use search page (individual clock pages are members-only)
        const tcgDatabaseUrl = 'https://tcg.ahsoc.org/search-the-database/';

        if (ahs.ahsId) {
            rows.push(`<tr><td>AHS Reference</td><td><a href="${tcgDatabaseUrl}" target="_blank" rel="noopener" class="ahs-ref-link">#${ahs.ahsId}</a></td></tr>`);
        }
        if (ahs.clockmaker) {
            rows.push(`<tr><td>Clockmaker</td><td>${ahs.clockmaker}</td></tr>`);
        }
        if (ahs.constructionDate) {
            rows.push(`<tr><td>Construction Date</td><td>${ahs.constructionDate}</td></tr>`);
        }
        if (ahs.buildingType) {
            rows.push(`<tr><td>Building Type</td><td>${ahs.buildingType}</td></tr>`);
        }
        if (ahs.buildingName) {
            rows.push(`<tr><td>Building Name</td><td>${ahs.buildingName}</td></tr>`);
        }
        if (ahs.dedication) {
            rows.push(`<tr><td>Dedication</td><td>${ahs.dedication}</td></tr>`);
        }
        if (ahs.ahsCondition) {
            rows.push(`<tr><td>TCG Survey Condition</td><td>${ahs.ahsCondition}</td></tr>`);
        }
        if (ahs.surveyDate) {
            rows.push(`<tr><td>Survey Date</td><td>${ahs.surveyDate}</td></tr>`);
        }
        if (ahs.recorder) {
            rows.push(`<tr><td>Surveyed By</td><td>${ahs.recorder}</td></tr>`);
        }
        if (ahs.ahsNotes) {
            rows.push(`<tr><td>Survey Notes</td><td>${ahs.ahsNotes}</td></tr>`);
        }

        return `
            <div class="ahs-attribution-section">
                <h3>Turret Clock Guild Survey Data</h3>
                <p class="ahs-attribution-intro">
                    This clock record was documented by the <strong>Turret Clock Guild</strong>, part of the
                    <a href="https://www.ahsoc.org/groups/turret-clock-group/" target="_blank" rel="noopener">Antiquarian Horological Society</a>.
                    The TCG has surveyed over 7,600 turret clocks across the UK.
                </p>
                <table class="ahs-data-table">
                    <tbody>
                        ${rows.join('')}
                    </tbody>
                </table>
                <p class="ahs-status-notice">
                    <strong>Note:</strong> This clock was marked as "${ahs.ahsCondition || 'derelict'}" at the time of survey.
                    The mechanism may have since been repaired or replaced. If you have current information about this clock,
                    please add a status update via the comments.
                </p>
                <div class="ahs-database-link">
                    <a href="${tcgDatabaseUrl}" target="_blank" rel="noopener" class="btn-tcg-database">
                        View in TCG Database
                    </a>
                </div>
            </div>
        `;
    }

    /**
     * Render Creative Commons licensing information
     * @returns {string} HTML for CC licensing display
     */
    renderCCLicensing() {
        // Check for Geograph/external CC attribution HTML (uppercase CC_LICENSE or lowercase ccLicense)
        const ccLicenseHtml = this.clock.CC_LICENSE || this.clock.ccLicense;

        // Check if user is authenticated
        const userInfo = this.getUserInfo();
        const isAuthenticated = !!userInfo && !!localStorage.getItem('authToken');

        // Only show report button to authenticated users
        const reportButton = isAuthenticated ? `
            <button class="cc-report-btn" onclick="window.ReportingUtils.showReportModal('${this.clock.slug || this.clock.id}', 'clock')" title="Report inappropriate content">
                Report
            </button>
        ` : '';

        // If we have custom HTML attribution (e.g., from Geograph), use it
        if (ccLicenseHtml && ccLicenseHtml.includes('<a')) {
            return `
                <div class="clock-cc-licensing">
                    <div class="cc-attribution">
                        ${ccLicenseHtml}
                    </div>
                    ${reportButton}
                </div>
            `;
        }

        // Otherwise, show default CC BY 4.0 badge
        return `
            <div class="clock-cc-licensing">
                <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener" class="cc-license-badge">
                    <img src="https://mirrors.creativecommons.org/presskit/icons/cc.svg" alt="Creative Commons" class="cc-icon">
                    <img src="https://mirrors.creativecommons.org/presskit/icons/by.svg" alt="Attribution" class="cc-icon">
                    <span>CC BY 4.0</span>
                </a>
                ${reportButton}
            </div>
        `;
    }
}

// Global function for viewing existing deep research
window.viewDeepResearch = function(clockId) {
    console.log('🔧 viewDeepResearch called with clockId:', clockId);
    if (typeof window.startDeepResearch === 'function') {
        window.startDeepResearch(clockId);
    } else {
        console.error('startDeepResearch function not available');
    }
};

// Global function for cancelling deep research
window.cancelDeepResearch = async function(clockId) {
    console.log('🔧 cancelDeepResearch called with clockId:', clockId);
    
    if (!confirm('Are you sure you want to cancel the current deep research? This will stop the research process and you can start a new one.')) {
        return;
    }
    
    try {
        const DEEP_RESEARCH_API_URL = 'https://rvnct9bwt6.execute-api.us-east-1.amazonaws.com/prod/deep-research/cancel';
        console.log('🌐 Cancelling research via API:', DEEP_RESEARCH_API_URL);
        
        const response = await fetch(DEEP_RESEARCH_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                clockId: clockId
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('📥 Cancel response:', result);
        
        if (result.success) {
            console.log('✅ Deep research cancelled successfully');
            alert('Deep research cancelled successfully. You can now start a new research.');
            
            // Refresh the page to update the buttons
            window.location.reload();
        } else {
            throw new Error(result.message || 'Failed to cancel research');
        }

    } catch (error) {
        console.error('❌ Error cancelling deep research:', error);
        alert('Failed to cancel deep research: ' + error.message);
    }
};

// Initialize the page when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.clockViewPage = new ClockViewPage();
});

// Make toggleFollow and toggleLike globally accessible
window.clockView = {
    toggleFollow: function() {
        if (window.clockViewPage) {
            window.clockViewPage.toggleFollow();
        }
    },
    toggleLike: function() {
        if (window.clockViewPage) {
            window.clockViewPage.toggleLike();
        }
    }
};
